import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';

export type DashboardTableCell = string | number;

export interface DashboardTableDialogPagination {
    page: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
}

export interface DashboardTableDialogData {
    title: string;
    headers: string[];
    rows: DashboardTableCell[][];
    centerColumns?: number[];
    rowGroupLabels?: string[];
    rowSubGroupLabels?: string[];
    pagination?: DashboardTableDialogPagination;
    continuationNoticeLabel?: string;
    closeButtonAriaLabel?: string;
    noRecordsFoundLabel?: string;
    recordCountSingularLabel?: string;
    recordsCountTemplate?: string;
}

@Component({
    selector: 'app-dashboard-table-dialog',
    templateUrl: './dashboard-table-dialog.component.html',
    styleUrls: ['./dashboard-table-dialog.component.scss']
})
export class DashboardTableDialogComponent {
    @ViewChild('tableScroll') tableScrollRef?: ElementRef<HTMLElement>;
    data: DashboardTableDialogData;
    recordsCountLabel: string;
    closeButtonAriaLabel: string;
    noRecordsFoundLabel: string;
    columnAlignments: ('left' | 'right' | 'center')[];
    groupHeaderBeforeRow: boolean[];
    subGroupBoundaryBeforeRow: boolean[];
    continuationRow: boolean[];

    constructor(
        @Inject(MAT_DIALOG_DATA) data: DashboardTableDialogData,
        private translateService: TranslateService
    ) {
        this.data = data;
        this.recordsCountLabel = '';
        this.closeButtonAriaLabel = '';
        this.noRecordsFoundLabel = '';
        this.columnAlignments = [];
        this.groupHeaderBeforeRow = [];
        this.subGroupBoundaryBeforeRow = [];
        this.continuationRow = [];
        this.recomputeDerivedState();
    }

    updatePage(data: DashboardTableDialogData): void {
        this.data = data;
        this.recomputeDerivedState();
        this.tableScrollRef?.nativeElement.scrollTo({ top: 0 });
    }

    onPageEvent(event: PageEvent): void {
        this.data.pagination?.onPageChange(event.pageIndex + 1);
    }

    get paginationPageIndex(): number {
        return (this.data.pagination?.page ?? 1) - 1;
    }

    get showPagination(): boolean {
        const pagination = this.data.pagination;
        return !!pagination && pagination.totalCount > pagination.pageSize;
    }

    trackByIndex(index: number): number {
        return index;
    }

    private recomputeDerivedState(): void {
        const data = this.data;

        this.closeButtonAriaLabel = data.closeButtonAriaLabel ?? this.translateService.instant('consumptionTicketSales.dashboard.closeModal');
        this.noRecordsFoundLabel = data.noRecordsFoundLabel ?? this.translateService.instant('consumptionTicketSales.dashboard.noRecordsFound');

        const count = data.pagination?.totalCount ?? data.rows.length;
        const singularLabel = data.recordCountSingularLabel ?? this.translateService.instant('consumptionTicketSales.dashboard.recordCountSingular');
        this.recordsCountLabel = count === 1
            ? singularLabel
            : (data.recordsCountTemplate?.replace('{count}', String(count))
                ?? this.translateService.instant('consumptionTicketSales.dashboard.recordsCount', { count }));

        const centerColumns = new Set(data.centerColumns);
        this.columnAlignments = data.headers.map((_, columnIndex) => {
            if (centerColumns.has(columnIndex)) return 'center';
            return data.rows.length && data.rows.every(row => typeof row[columnIndex] === 'number')
                ? 'right'
                : 'left';
        });

        const groupLabels = data.rowGroupLabels;
        const hasMultipleGroups = !!groupLabels && new Set(groupLabels).size > 1;
        this.groupHeaderBeforeRow = data.rows.map((_, rowIndex) =>
            hasMultipleGroups && (rowIndex === 0 || groupLabels![rowIndex] !== groupLabels![rowIndex - 1])
        );

        const subGroupLabels = data.rowSubGroupLabels;
        this.subGroupBoundaryBeforeRow = data.rows.map((_, rowIndex) =>
            !!subGroupLabels
            && rowIndex > 0
            && !this.groupHeaderBeforeRow[rowIndex]
            && subGroupLabels[rowIndex] !== subGroupLabels[rowIndex - 1]
        );

        this.continuationRow = data.rows.map((_, rowIndex) =>
            !!data.continuationNoticeLabel
            && !!subGroupLabels
            && subGroupLabels[rowIndex] === subGroupLabels[0]
        );
    }
}
